/**
 * One starter template: its details, sections and lines.
 *
 * Every control saves on its own request, and the page re-reads the template
 * afterwards, so what is on screen is always what the server holds. Moving a
 * section or line sends the full new order; the server refuses an order that
 * omits or repeats an id.
 */

import { Button } from "@shared-ui/button";
import { Input } from "@shared-ui/input";
import { Label } from "@shared-ui/label";
import { Skeleton } from "@shared-ui/skeleton";
import { Switch } from "@shared-ui/switch";
import { Textarea } from "@shared-ui/textarea";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAdminAuthStore } from "@/features/auth/store/admin-auth.store";
import { hasPermission } from "@/lib/permissions";
import { ItemDialog } from "../components/item-dialog";
import { describePrice, describeQuantity } from "../lib/rules";
import {
	createSection,
	deleteItem,
	deleteSection,
	deleteTemplate,
	getTemplate,
	reorderItems,
	reorderSections,
	type TemplateDetail,
	type TemplateItem,
	updateSection,
	updateTemplate,
} from "../services/boq-templates-api.service";
import { TEMPLATES_QUERY_KEY } from "./templates-dashboard";

/** The API caps `sortOrder` here. */
const MAX_SORT_ORDER = 10_000;

function move<T>(list: T[], from: number, to: number): T[] {
	const next = [...list];
	const [x] = next.splice(from, 1);
	next.splice(to, 0, x);
	return next;
}

export function BoqTemplateEditorPage() {
	const { templateId = "" } = useParams();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const operator = useAdminAuthStore((s) => s.operator);
	const canWrite = hasPermission(operator, "admin:boq-templates:write");
	const key = ["admin-boq-template", templateId];

	const query = useQuery({
		queryKey: key,
		queryFn: () => getTemplate(templateId),
	});

	const refresh = async () => {
		await queryClient.invalidateQueries({ queryKey: key });
		await queryClient.invalidateQueries({ queryKey: TEMPLATES_QUERY_KEY });
	};
	const run = useMutation({
		mutationFn: (fn: () => Promise<unknown>) => fn(),
		onSuccess: refresh,
	});

	const [itemDialog, setItemDialog] = useState<{
		sectionId: string;
		item?: TemplateItem;
	} | null>(null);
	const [newSection, setNewSection] = useState("");

	if (query.isLoading) return <Skeleton className="h-64 w-full" />;
	if (query.isError || !query.data) {
		return (
			<p role="alert" className="text-sm text-red-700">
				Could not load this template.
			</p>
		);
	}
	const t = query.data;

	return (
		<div className="grid gap-5">
			<Link to="/boq-templates" className="text-sm text-muted-foreground">
				← All templates
			</Link>

			<TemplateDetails
				// Remount on a server-side change so the draft re-seeds from it.
				key={`${t.id}-${t.name}-${t.slug}-${t.description}-${t.projectScope}-${t.supportedProviderRoles.join()}-${t.defaultTier}-${t.sortOrder}`}
				template={t}
				canWrite={canWrite}
				onSave={(patch) => run.mutate(() => updateTemplate(t.id, patch))}
				onDelete={async () => {
					if (!window.confirm(`Delete "${t.name}"? This cannot be undone.`))
						return;
					try {
						await deleteTemplate(t.id);
						await queryClient.invalidateQueries({
							queryKey: TEMPLATES_QUERY_KEY,
						});
						navigate("/boq-templates");
					} catch {
						// The API client has already toasted the reason.
					}
				}}
			/>

			<div className="flex items-center gap-3 rounded-2xl border bg-white p-4">
				<Switch
					aria-label="Template is active"
					checked={t.isActive}
					disabled={!canWrite || run.isPending}
					onCheckedChange={(v) =>
						run.mutate(() => updateTemplate(t.id, { isActive: v }))
					}
				/>
				<div className="text-sm">
					<p className="font-medium">{t.isActive ? "Active" : "Inactive"}</p>
					<p className="text-muted-foreground">
						{t.isActive
							? "Designers and contractors can generate a BOQ from this."
							: "Hidden from designers and contractors."}
					</p>
				</div>
			</div>

			{t.sections.map((s, si) => (
				<section
					key={s.id}
					className="grid gap-3 rounded-2xl border bg-white p-4"
				>
					<div className="flex items-center justify-between gap-2">
						<SectionName
							name={s.name}
							canWrite={canWrite}
							onRename={(name) =>
								run.mutate(() => updateSection(s.id, { name }))
							}
						/>
						{canWrite && (
							<div className="flex gap-1">
								<Button
									size="sm"
									variant="ghost"
									aria-label={`Move ${s.name} up`}
									disabled={si === 0 || run.isPending}
									onClick={() =>
										run.mutate(() =>
											reorderSections(
												t.id,
												move(t.sections, si, si - 1).map((x) => x.id),
											),
										)
									}
								>
									↑
								</Button>
								<Button
									size="sm"
									variant="ghost"
									aria-label={`Move ${s.name} down`}
									disabled={si === t.sections.length - 1 || run.isPending}
									onClick={() =>
										run.mutate(() =>
											reorderSections(
												t.id,
												move(t.sections, si, si + 1).map((x) => x.id),
											),
										)
									}
								>
									↓
								</Button>
								<Button
									size="sm"
									variant="ghost"
									aria-label={`Delete section ${s.name}`}
									onClick={() => {
										if (
											window.confirm(
												`Delete "${s.name}" and its ${s.items.length} line(s)?`,
											)
										)
											run.mutate(() => deleteSection(s.id));
									}}
								>
									Delete
								</Button>
							</div>
						)}
					</div>

					{(canWrite || s.description) && (
						<SectionDescription
							description={s.description}
							canWrite={canWrite}
							onChange={(description) =>
								run.mutate(() => updateSection(s.id, { description }))
							}
						/>
					)}

					{s.items.length === 0 ? (
						<p className="text-sm text-muted-foreground">No lines yet.</p>
					) : (
						<ul className="grid gap-2">
							{s.items.map((it, ii) => (
								<li
									key={it.id}
									className="flex items-center justify-between gap-3 rounded-xl border px-3 py-2 text-sm"
								>
									<div>
										<p className="font-medium">
											{it.labelOverride ?? it.productName}
											{it.isOptional && (
												<span className="ml-2 text-xs text-muted-foreground">
													optional
												</span>
											)}
										</p>
										<p className="text-xs text-muted-foreground">
											{it.productSku ?? "no SKU"} · qty{" "}
											{describeQuantity(it.quantityRule)} · price{" "}
											{describePrice(it.priceRule)}
											{it.variantSku ? ` · variant ${it.variantSku}` : ""}
											{it.variantUnavailable
												? " · variant is retired or off"
												: ""}
											{it.defaultUnit ? ` · ${it.defaultUnit}` : ""}
											{it.productStatus !== "active"
												? ` · product is ${it.productStatus}`
												: ""}
										</p>
									</div>
									{canWrite && (
										<div className="flex shrink-0 gap-1">
											<Button
												size="sm"
												variant="ghost"
												aria-label={`Move ${it.labelOverride ?? it.productName} up`}
												disabled={ii === 0 || run.isPending}
												onClick={() =>
													run.mutate(() =>
														reorderItems(
															s.id,
															move(s.items, ii, ii - 1).map((x) => x.id),
														),
													)
												}
											>
												↑
											</Button>
											<Button
												size="sm"
												variant="ghost"
												aria-label={`Move ${it.labelOverride ?? it.productName} down`}
												disabled={ii === s.items.length - 1 || run.isPending}
												onClick={() =>
													run.mutate(() =>
														reorderItems(
															s.id,
															move(s.items, ii, ii + 1).map((x) => x.id),
														),
													)
												}
											>
												↓
											</Button>
											<Button
												size="sm"
												variant="outline"
												onClick={() =>
													setItemDialog({ sectionId: s.id, item: it })
												}
											>
												Edit
											</Button>
											<Button
												size="sm"
												variant="ghost"
												aria-label={`Remove ${it.labelOverride ?? it.productName}`}
												onClick={() => run.mutate(() => deleteItem(it.id))}
											>
												Remove
											</Button>
										</div>
									)}
								</li>
							))}
						</ul>
					)}

					{canWrite && (
						<div>
							<Button
								size="sm"
								variant="outline"
								onClick={() => setItemDialog({ sectionId: s.id })}
							>
								Add line
							</Button>
						</div>
					)}
				</section>
			))}

			{canWrite && (
				<form
					className="flex items-end gap-2"
					onSubmit={(e) => {
						e.preventDefault();
						const name = newSection.trim();
						if (!name) return;
						run.mutate(() => createSection(t.id, { name }), {
							onSuccess: () => setNewSection(""),
						});
					}}
				>
					<div className="grid flex-1 gap-1">
						<Label htmlFor="new-section">New section</Label>
						<Input
							id="new-section"
							placeholder="e.g. Flooring"
							value={newSection}
							onChange={(e) => setNewSection(e.target.value)}
						/>
					</div>
					<Button type="submit" disabled={!newSection.trim() || run.isPending}>
						Add section
					</Button>
				</form>
			)}

			{itemDialog && (
				<ItemDialog
					sectionId={itemDialog.sectionId}
					item={itemDialog.item}
					onClose={() => setItemDialog(null)}
					onSaved={() => void refresh()}
				/>
			)}
		</div>
	);
}

function SectionName({
	name,
	canWrite,
	onRename,
}: {
	name: string;
	canWrite: boolean;
	onRename: (name: string) => void;
}) {
	const [draft, setDraft] = useState(name);
	if (!canWrite) return <h3 className="font-semibold">{name}</h3>;
	return (
		<Input
			aria-label="Section name"
			className="max-w-sm font-semibold"
			value={draft}
			onChange={(e) => setDraft(e.target.value)}
			onBlur={() => {
				const next = draft.trim();
				if (next && next !== name) onRename(next);
				else setDraft(name);
			}}
		/>
	);
}

function SectionDescription({
	description,
	canWrite,
	onChange,
}: {
	description: string | null;
	canWrite: boolean;
	onChange: (description: string | null) => void;
}) {
	const [draft, setDraft] = useState(description ?? "");
	if (!canWrite) {
		return <p className="text-sm text-muted-foreground">{description}</p>;
	}
	return (
		<Input
			aria-label="Section description"
			placeholder="Optional note shown with this section"
			value={draft}
			onChange={(e) => setDraft(e.target.value)}
			onBlur={() => {
				const next = draft.trim() || null;
				if (next !== (description?.trim() || null)) onChange(next);
				else setDraft(description ?? "");
			}}
		/>
	);
}

function TemplateDetails({
	template,
	canWrite,
	onSave,
	onDelete,
}: {
	template: TemplateDetail;
	canWrite: boolean;
	onSave: (patch: {
		name: string;
		slug: string;
		description: string | null;
		projectScope: TemplateDetail["projectScope"];
		supportedProviderRoles: TemplateDetail["supportedProviderRoles"];
		defaultTier: TemplateDetail["defaultTier"];
		sortOrder: number;
	}) => void;
	onDelete: () => void;
}) {
	const [name, setName] = useState(template.name);
	const [slug, setSlug] = useState(template.slug);
	const [description, setDescription] = useState(template.description ?? "");
	const [scope, setScope] = useState(template.projectScope ?? "");
	const [roles, setRoles] = useState(template.supportedProviderRoles);
	const [tier, setTier] = useState(template.defaultTier);
	const [sortOrder, setSortOrder] = useState(String(template.sortOrder));
	const sortOrderValid =
		/^\d+$/.test(sortOrder.trim()) && Number(sortOrder) <= MAX_SORT_ORDER;

	const toggleRole = (r: "designer" | "contractor") =>
		setRoles((cur) =>
			cur.includes(r) ? cur.filter((x) => x !== r) : [...cur, r],
		);

	return (
		<div className="grid gap-3 rounded-2xl border bg-white p-4">
			<h2 className="text-2xl font-semibold tracking-tight">{template.name}</h2>
			<div className="grid gap-3 md:grid-cols-2">
				<div className="grid gap-1">
					<Label htmlFor="t-name">Name</Label>
					<Input
						id="t-name"
						value={name}
						disabled={!canWrite}
						onChange={(e) => setName(e.target.value)}
					/>
				</div>
				<div className="grid gap-1">
					<Label htmlFor="t-slug">Slug</Label>
					<Input
						id="t-slug"
						value={slug}
						disabled={!canWrite}
						onChange={(e) => setSlug(e.target.value)}
					/>
				</div>
				<div className="grid gap-1 md:col-span-2">
					<Label htmlFor="t-desc">Description</Label>
					<Textarea
						id="t-desc"
						value={description}
						disabled={!canWrite}
						onChange={(e) => setDescription(e.target.value)}
					/>
				</div>
				<div className="grid gap-1">
					<Label htmlFor="t-scope">Project scope</Label>
					<select
						id="t-scope"
						className="h-9 rounded-md border border-input bg-transparent px-2 text-sm"
						value={scope}
						disabled={!canWrite}
						onChange={(e) => setScope(e.target.value as typeof scope)}
					>
						<option value="">Any scope</option>
						<option value="full_home">Full home</option>
						<option value="partial">Partial</option>
					</select>
				</div>
				<div className="grid gap-1">
					<Label htmlFor="t-tier">Default tier</Label>
					<select
						id="t-tier"
						className="h-9 rounded-md border border-input bg-transparent px-2 text-sm"
						value={tier}
						disabled={!canWrite}
						onChange={(e) => setTier(e.target.value as typeof tier)}
					>
						<option value="standard">Standard</option>
						<option value="premium">Premium</option>
						<option value="luxury">Luxury</option>
					</select>
				</div>
				<div className="grid gap-1">
					<Label htmlFor="t-order">List order</Label>
					<Input
						id="t-order"
						inputMode="numeric"
						value={sortOrder}
						disabled={!canWrite}
						aria-invalid={!sortOrderValid}
						onChange={(e) => setSortOrder(e.target.value)}
					/>
					{!sortOrderValid && (
						<p className="text-xs text-red-700">
							A whole number from 0 to {MAX_SORT_ORDER}. Lower numbers are
							listed first.
						</p>
					)}
				</div>
				<fieldset className="flex items-center gap-4 text-sm">
					<legend className="mb-1 text-sm font-medium">Offered to</legend>
					{(["designer", "contractor"] as const).map((r) => (
						<label key={r} className="flex items-center gap-2 capitalize">
							<input
								type="checkbox"
								checked={roles.includes(r)}
								disabled={!canWrite}
								onChange={() => toggleRole(r)}
							/>
							{r}
						</label>
					))}
				</fieldset>
			</div>
			{canWrite && (
				<div className="flex justify-between">
					<Button variant="outline" onClick={onDelete}>
						Delete template
					</Button>
					<Button
						disabled={
							roles.length === 0 || !name.trim() || !slug || !sortOrderValid
						}
						onClick={() =>
							onSave({
								name: name.trim(),
								slug,
								description: description.trim() || null,
								projectScope: (scope || null) as TemplateDetail["projectScope"],
								supportedProviderRoles: roles,
								defaultTier: tier,
								sortOrder: Number(sortOrder),
							})
						}
					>
						Save details
					</Button>
				</div>
			)}
		</div>
	);
}
